import { useEffect, useMemo, useState, useCallback } from 'react';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import StepUpModal from '@/components/StepUpModal';
import {
  ShieldCheck, Activity, KeyRound, UserCheck, UserPlus, MailCheck, LogOut,
  Wrench, AlertTriangle, Search, Filter, RefreshCw, Laptop, Trash2, Ban,
  CheckCircle2, XCircle, ChevronRight, ArrowLeft,
} from 'lucide-react';

function StatCard({ label, value, hint, icon: Icon, tone = 'default' }) {
  const toneCls = {
    default: 'bg-white', warn: 'bg-amber-50', danger: 'bg-red-50', good: 'bg-emerald-50',
  }[tone] || 'bg-white';
  return (
    <div className={`${toneCls} rounded-2xl p-5 card-soft`}>
      <div className="flex items-start justify-between">
        <p className="text-[11px] uppercase tracking-widest text-navy/55 font-semibold">{label}</p>
        {Icon && <Icon size={14} className="text-navy/40"/>}
      </div>
      <p className="mt-2 font-heading text-3xl">{value}</p>
      {hint && <p className="mt-1 text-[11px] text-navy/45">{hint}</p>}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const TABS = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'audit', label: 'Audit log', icon: Wrench },
  { id: 'sessions', label: 'Sessions', icon: Laptop },
  { id: 'alerts', label: 'Alerts', icon: AlertTriangle },
  { id: 'users', label: 'User search', icon: Search },
];

export default function AdminSecurity() {
  const { user } = useAuth();
  const [tab, setTab] = useState('overview');
  const [stepup, setStepup] = useState(null); // { purpose, onSuccess }

  // Called by any sensitive action that hits 403 stepup_*
  const openStepUp = useCallback((purpose, onSuccess) => {
    setStepup({ purpose, onSuccess });
  }, []);

  if (!user?.is_admin) return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen bg-[#F9F8F6] p-5 lg:p-10 space-y-6" data-testid="admin-security-page">
      <Link to="/app/profile" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-smooth">
        <ArrowLeft size={14}/> Back to Profile
      </Link>

      <header className="max-w-6xl">
        <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold flex items-center gap-1.5">
          <ShieldCheck size={12}/> Administrator · Security
        </p>
        <h1 className="font-heading text-4xl lg:text-5xl mt-2 leading-tight">Security dashboard</h1>
        <p className="text-navy/65 text-base lg:text-lg mt-2 max-w-3xl leading-relaxed">
          Monitor auth activity, review suspicious behavior, and take safe administrative action across the Brotherhood+ member base.
        </p>
      </header>

      <nav className="flex gap-2 overflow-x-auto pb-1" role="tablist" data-testid="admin-security-tabs">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button key={t.id} role="tab" aria-selected={active}
              onClick={() => setTab(t.id)} data-testid={`admin-security-tab-${t.id}`}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-2 transition-smooth ${
                active ? 'bg-navy text-white' : 'bg-white text-navy hover:bg-beige border border-navy/10'
              }`}>
              <t.icon size={14}/> {t.label}
            </button>
          );
        })}
      </nav>

      <main className="max-w-6xl">
        {tab === 'overview' && <OverviewPane/>}
        {tab === 'audit' && <AuditLogPane/>}
        {tab === 'sessions' && <SessionsPane openStepUp={openStepUp}/>}
        {tab === 'alerts' && <AlertsPane/>}
        {tab === 'users' && <UsersPane openStepUp={openStepUp}/>}
      </main>

      <StepUpModal
        open={!!stepup}
        purpose={stepup?.purpose || 'admin'}
        has2FA={!!user?.totp_enabled}
        onSuccess={() => { const cb = stepup?.onSuccess; setStepup(null); cb?.(); }}
        onClose={() => setStepup(null)}
      />
    </div>
  );
}

/* ─────────────────────── OVERVIEW ─────────────────────── */
function OverviewPane() {
  const [data, setData] = useState(null);
  const load = async () => {
    try { setData((await http.get('/admin/security/overview')).data); }
    catch { toast.error('Could not load overview.'); }
  };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-navy/50">Loading…</div>;

  const w = data.windows || {};
  const t = data.totals || {};
  return (
    <div className="space-y-6" data-testid="admin-security-overview">
      <section>
        <h2 className="font-heading text-xl mb-3">Fleet</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Total users" value={t.users ?? '—'} icon={UserPlus}/>
          <StatCard label="Verified" value={t.verified_users ?? '—'} tone="good" icon={UserCheck}
                    hint={`${t.pending_verifications ?? 0} pending`}/>
          <StatCard label="Active sessions" value={t.active_sessions ?? '—'} icon={Laptop}/>
          <StatCard label="2FA enabled" value={t.totp_enabled_users ?? '—'} tone="good" icon={ShieldCheck}/>
        </div>
      </section>

      {['24h','7d','30d'].map((k) => {
        const win = w[k] || {};
        const tone = (win.failed_logins || 0) > 20 ? 'warn' : 'default';
        return (
          <section key={k}>
            <h2 className="font-heading text-xl mb-3">Last {k}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Logins" value={win.logins ?? 0} icon={KeyRound}/>
              <StatCard label="Failed logins" value={win.failed_logins ?? 0} tone={tone} icon={XCircle}/>
              <StatCard label="Lockouts" value={win.lockouts ?? 0} tone={(win.lockouts||0) ? 'warn' : 'default'} icon={Ban}/>
              <StatCard label="Password resets" value={win.password_resets ?? 0} icon={KeyRound}/>
              <StatCard label="Password changes" value={win.password_changes ?? 0} icon={CheckCircle2}/>
              <StatCard label="Registrations" value={win.registrations ?? 0} icon={UserPlus}/>
              <StatCard label="Emails verified" value={win.email_verified ?? 0} icon={MailCheck}/>
              <StatCard label="Session revocations" value={win.session_revocations ?? 0} icon={LogOut}/>
            </div>
          </section>
        );
      })}

      <p className="text-[11px] text-navy/40">
        Geo provider: <span className="font-medium">{data.geo_provider || 'null'}</span> · Generated {formatDate(data.generated_at)}
      </p>
    </div>
  );
}

/* ─────────────────────── AUDIT LOG ─────────────────────── */
function AuditLogPane() {
  const [q, setQ] = useState('');
  const [event, setEvent] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (event) params.set('event', event);
      params.set('limit', '50');
      params.set('offset', String(offset));
      const { data } = await http.get(`/admin/security/audit-log?${params}`);
      setRows(data.events || []); setTotal(data.total || 0);
    } catch { toast.error('Could not load audit log.'); }
    finally { setLoading(false); }
  }, [q, event, offset]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-security-audit">
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40 pointer-events-none"/>
          <input placeholder="Search email or event…" value={q} onChange={(e)=>{ setQ(e.target.value); setOffset(0); }}
            className="w-full bg-white border border-navy/10 rounded-full pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            data-testid="audit-search"/>
        </div>
        <select value={event} onChange={(e)=>{ setEvent(e.target.value); setOffset(0); }}
          className="bg-white border border-navy/10 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
          data-testid="audit-event-filter">
          <option value="">All events</option>
          <option value="auth.login">Login</option>
          <option value="auth.login_failed">Failed login</option>
          <option value="auth.lockout">Lockout</option>
          <option value="auth.register">Register</option>
          <option value="auth.password_reset_requested">Reset request</option>
          <option value="auth.password_reset">Reset done</option>
          <option value="auth.password_changed">Password changed</option>
          <option value="auth.2fa_enabled">2FA enabled</option>
          <option value="auth.2fa_disabled">2FA disabled</option>
          <option value="admin.force_logout">Admin: force logout</option>
          <option value="admin.user_suspended">Admin: suspend</option>
        </select>
        <button onClick={load} className="bg-white text-navy border border-navy/10 rounded-full px-3 py-2 text-sm hover:bg-beige"
          data-testid="audit-refresh"><RefreshCw size={14}/></button>
      </div>

      <div className="bg-white rounded-2xl card-soft overflow-hidden">
        <div className="grid grid-cols-12 text-[11px] uppercase tracking-widest text-navy/50 px-4 py-2 border-b border-navy/5">
          <div className="col-span-3">Time</div>
          <div className="col-span-4">Event</div>
          <div className="col-span-3">Who</div>
          <div className="col-span-2">IP</div>
        </div>
        {loading ? <div className="p-6 text-navy/50">Loading…</div> :
         rows.length === 0 ? <div className="p-6 text-navy/50">No matching events.</div> :
         rows.map((e) => (
          <div key={e.id} className="grid grid-cols-12 gap-2 px-4 py-2 text-sm border-b border-navy/5 hover:bg-beige/40" data-testid={`audit-row-${e.id}`}>
            <div className="col-span-3 text-navy/60 font-mono text-xs">{formatDate(e.at)}</div>
            <div className="col-span-4 font-medium">{e.event}{e.admin_action && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gold/15 text-gold">admin</span>}</div>
            <div className="col-span-3 truncate text-navy/70">{e.email || e.user_id || '—'}</div>
            <div className="col-span-2 text-navy/50 font-mono text-xs">{e.ip_masked || '—'}</div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2 text-sm text-navy/60">
        <span>{total} total</span>
        <button disabled={offset === 0} onClick={()=>setOffset(Math.max(0, offset-50))}
          className="ml-auto bg-white border border-navy/10 rounded-full px-3 py-1 disabled:opacity-40">‹ Prev</button>
        <button disabled={offset + rows.length >= total} onClick={()=>setOffset(offset+50)}
          className="bg-white border border-navy/10 rounded-full px-3 py-1 disabled:opacity-40">Next ›</button>
      </div>
    </div>
  );
}

/* ─────────────────────── SESSIONS ─────────────────────── */
function SessionsPane({ openStepUp }) {
  const [data, setData] = useState({ sessions: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setData((await http.get('/admin/security/sessions?limit=100')).data); }
    catch { toast.error('Could not load sessions.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const forceLogout = async (userId) => {
    if (!window.confirm('Sign out every device for this user?')) return;
    const attempt = async () => {
      try {
        await http.post(`/admin/security/users/${userId}/force-logout`, { reason: 'Admin dashboard' });
        toast.success('All sessions revoked.'); await load();
      } catch (e) {
        const d = e?.response?.data?.detail;
        if (e?.response?.status === 403 && d?.code?.startsWith('stepup_')) {
          openStepUp(d.purpose || 'admin.force_logout', attempt); return;
        }
        toast.error(typeof d === 'string' ? d : 'Failed.');
      }
    };
    await attempt();
  };

  return (
    <div className="bg-white rounded-2xl card-soft overflow-hidden" data-testid="admin-security-sessions">
      <div className="grid grid-cols-12 text-[11px] uppercase tracking-widest text-navy/50 px-4 py-2 border-b border-navy/5">
        <div className="col-span-3">User</div>
        <div className="col-span-4">Device</div>
        <div className="col-span-2">Location</div>
        <div className="col-span-2">Last used</div>
        <div className="col-span-1 text-right">Actions</div>
      </div>
      {loading ? <div className="p-6 text-navy/50">Loading…</div> :
       data.sessions.length === 0 ? <div className="p-6 text-navy/50">No active sessions.</div> :
       data.sessions.map((s) => (
        <div key={s.jti} className="grid grid-cols-12 gap-2 px-4 py-2 text-sm border-b border-navy/5 items-center" data-testid={`session-row-${s.jti}`}>
          <div className="col-span-3 truncate">
            <div className="font-medium truncate">{s.user_name || '—'}</div>
            <div className="text-xs text-navy/50 truncate">{s.user_email}</div>
          </div>
          <div className="col-span-4 text-navy/70 truncate text-xs">{s.user_agent || '—'}</div>
          <div className="col-span-2 text-navy/60 text-xs">
            {s.country || '—'}{s.city ? ` · ${s.city}` : ''}
            <div className="text-navy/40 font-mono">{s.ip_masked}</div>
          </div>
          <div className="col-span-2 text-navy/50 text-xs">{formatDate(s.last_used_at)}</div>
          <div className="col-span-1 text-right">
            <button onClick={() => forceLogout(s.user_id)}
              className="text-xs bg-red-50 text-red-700 rounded-full px-2.5 py-1 hover:bg-red-100"
              data-testid={`session-force-logout-${s.jti}`}>
              <Trash2 size={11} className="inline mr-1"/> Sign out
            </button>
          </div>
        </div>
       ))}
      <div className="p-3 text-xs text-navy/50">{data.total} session{data.total === 1 ? '' : 's'} total</div>
    </div>
  );
}

/* ─────────────────────── ALERTS ─────────────────────── */
function AlertsPane() {
  const [status, setStatus] = useState('unresolved');
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    try { setRows((await http.get(`/admin/security/alerts?status=${status}`)).data.alerts || []); }
    catch { toast.error('Could not load alerts.'); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const review = async (id) => {
    const note = window.prompt('Optional review note:');
    try { await http.post(`/admin/security/alerts/${id}/review`, { note: note || null }); toast.success('Marked reviewed.'); await load(); }
    catch { toast.error('Could not mark reviewed.'); }
  };

  return (
    <div data-testid="admin-security-alerts">
      <div className="flex gap-2 mb-3">
        {['unresolved', 'resolved', 'all'].map((s) => (
          <button key={s} onClick={()=>setStatus(s)}
            className={`rounded-full px-3 py-1 text-xs font-medium ${status===s?'bg-navy text-white':'bg-white border border-navy/10 text-navy hover:bg-beige'}`}>
            {s.charAt(0).toUpperCase()+s.slice(1)}
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 card-soft text-navy/50 text-center">No {status} alerts.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((a) => {
            const sev = a.severity === 'high' ? 'bg-red-50 text-red-700 border-red-200' :
                        a.severity === 'medium' ? 'bg-amber-50 text-amber-800 border-amber-200' :
                                                   'bg-navy/5 text-navy border-navy/10';
            return (
              <div key={a.id} className={`rounded-2xl p-4 border ${sev} card-soft`} data-testid={`alert-${a.id}`}>
                <div className="flex items-start gap-3">
                  <AlertTriangle size={18} className="mt-0.5 shrink-0"/>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm capitalize">{a.type.replace(/_/g, ' ')}</p>
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/70 font-semibold">{a.severity}</span>
                    </div>
                    <p className="text-xs mt-0.5">
                      {a.email && <span className="font-mono">{a.email}</span>}
                      {a.user_id && <span className="font-mono">user:{a.user_id.slice(0,8)}…</span>}
                      {' · '}
                      {formatDate(a.at)}
                    </p>
                    <pre className="mt-2 text-[11px] bg-white/70 rounded-lg p-2 overflow-x-auto">{JSON.stringify(a.meta || {}, null, 2)}</pre>
                  </div>
                  {!a.reviewed && (
                    <button onClick={() => review(a.id)}
                      className="text-xs bg-white text-navy rounded-full px-3 py-1 border border-navy/15 hover:bg-beige">
                      Mark reviewed
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── USER SEARCH + DETAIL ─────────────────────── */
function UsersPane({ openStepUp }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);

  const search = async (e) => {
    e && e.preventDefault();
    if (!q.trim()) return;
    try { setResults((await http.get(`/admin/security/users/search?q=${encodeURIComponent(q)}`)).data.users || []); }
    catch { toast.error('Search failed.'); }
  };

  return (
    <div data-testid="admin-security-users">
      <form onSubmit={search} className="flex gap-2 mb-3">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40 pointer-events-none"/>
          <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search by email or name…"
            className="w-full bg-white border border-navy/10 rounded-full pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            data-testid="user-search-input"/>
        </div>
        <button className="bg-navy text-white rounded-full px-4 py-2 text-sm font-medium">Search</button>
      </form>

      {results.length > 0 && (
        <div className="bg-white rounded-2xl divide-y divide-navy/5 card-soft mb-4">
          {results.map((u) => (
            <button key={u.id} onClick={() => setSelected(u)}
              className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-beige/50"
              data-testid={`user-result-${u.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{u.name || '(no name)'}</span>
                  {u.is_admin && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-gold/15 text-gold font-semibold">admin</span>}
                  {u.disabled && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">suspended</span>}
                  {u.totp_enabled && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">2fa</span>}
                  {!u.email_verified && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">unverified</span>}
                </div>
                <span className="text-xs text-navy/55">{u.email}</span>
              </div>
              <ChevronRight size={16} className="text-navy/40"/>
            </button>
          ))}
        </div>
      )}

      {selected && <UserDetailCard userId={selected.id} onClose={() => setSelected(null)} openStepUp={openStepUp}/>}
    </div>
  );
}

function UserDetailCard({ userId, onClose, openStepUp }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData((await http.get(`/admin/security/users/${userId}/history`)).data); }
    catch { toast.error('Could not load user history.'); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-navy/50">Loading history…</div>;

  const u = data.user;

  const doAction = async (path, purpose, confirmMsg, reason) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    const r = reason ? { reason } : (window.prompt('Optional reason for the audit log:') || undefined);
    const attempt = async () => {
      try { await http.post(`/admin/security/users/${userId}/${path}`, r ? { reason: r } : {}); toast.success('Done.'); await load(); }
      catch (e) {
        const d = e?.response?.data?.detail;
        if (e?.response?.status === 403 && d?.code?.startsWith('stepup_')) {
          openStepUp(d.purpose || purpose, attempt); return;
        }
        toast.error(typeof d === 'string' ? d : 'Failed.');
      }
    };
    await attempt();
  };

  return (
    <div className="bg-white rounded-2xl card-soft p-5 space-y-4" data-testid="user-detail-card">
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-2xl">{u.name || '(no name)'}</h3>
            {u.is_admin && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-gold/15 text-gold font-semibold">admin</span>}
            {u.disabled && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">suspended</span>}
          </div>
          <p className="text-sm text-navy/60">{u.email}</p>
          <p className="text-xs text-navy/40 mt-1">Joined {formatDate(u.created_at)} · Role {u.role || 'member'} · {u.email_verified ? 'Email verified' : 'Not verified'} · 2FA {u.totp_enabled ? 'on' : 'off'}</p>
        </div>
        <button onClick={onClose} className="text-navy/50 hover:text-navy text-sm">Close</button>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => doAction('force-logout', 'admin.force_logout', 'Sign this user out of every device?')}
          className="text-xs bg-red-50 text-red-700 rounded-full px-3 py-1.5 hover:bg-red-100" data-testid="ud-force-logout">
          <LogOut size={12} className="inline mr-1"/> Force logout all
        </button>
        <button onClick={() => doAction('unlock', 'admin.unlock', 'Clear brute-force lockouts for this user?', 'admin dashboard')}
          className="text-xs bg-beige text-navy rounded-full px-3 py-1.5 hover:bg-beige/70" data-testid="ud-unlock">
          <KeyRound size={12} className="inline mr-1"/> Unlock account
        </button>
        {!u.disabled ? (
          <button onClick={() => doAction('suspend', 'admin.suspend', 'Suspend this account? User will be signed out and unable to sign in.')}
            className="text-xs bg-red-50 text-red-700 rounded-full px-3 py-1.5 hover:bg-red-100" data-testid="ud-suspend">
            <Ban size={12} className="inline mr-1"/> Suspend
          </button>
        ) : (
          <button onClick={() => doAction('unsuspend', 'admin.suspend', 'Reactivate this account?')}
            className="text-xs bg-emerald-50 text-emerald-700 rounded-full px-3 py-1.5 hover:bg-emerald-100" data-testid="ud-unsuspend">
            <CheckCircle2 size={12} className="inline mr-1"/> Unsuspend
          </button>
        )}
        {u.totp_enabled && (
          <button onClick={() => doAction('disable-2fa', 'admin.disable_2fa', 'Disable 2FA on this account? Should only be used after out-of-band identity verification.')}
            className="text-xs bg-amber-50 text-amber-800 rounded-full px-3 py-1.5 hover:bg-amber-100" data-testid="ud-disable-2fa">
            <ShieldCheck size={12} className="inline mr-1"/> Disable 2FA
          </button>
        )}
      </div>

      <details className="rounded-2xl bg-beige/50 p-4">
        <summary className="cursor-pointer font-medium text-sm">Active sessions ({data.active_sessions.length})</summary>
        <div className="mt-3 space-y-2 text-xs text-navy/70">
          {data.active_sessions.map((s) => (
            <div key={s.jti} className="bg-white rounded-lg p-2">
              <div className="truncate">{s.user_agent}</div>
              <div className="text-navy/50 font-mono">{s.ip_masked} · last used {formatDate(s.last_used_at)}</div>
            </div>
          ))}
          {data.active_sessions.length === 0 && <div className="text-navy/40">No active sessions.</div>}
        </div>
      </details>

      <details className="rounded-2xl bg-beige/50 p-4">
        <summary className="cursor-pointer font-medium text-sm">Recent security events ({data.events.length})</summary>
        <div className="mt-3 space-y-1 text-xs text-navy/70 max-h-96 overflow-y-auto">
          {data.events.map((e) => (
            <div key={e.id} className="flex gap-3 items-baseline">
              <span className="font-mono text-navy/40">{formatDate(e.at)}</span>
              <span className="font-medium">{e.event}</span>
              <span className="text-navy/40 font-mono">{e.ip_masked || ''}</span>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}
